/**
 * Parse MXNet graph format from Supabase into Netron-compatible format
 */

/**
 * Check if JSON structure matches MXNet format
 */
function isMXNetFormat(jsonData) {
  if (!jsonData || typeof jsonData !== 'object') {
    return false;
  }

  // MXNet models have these specific fields
  const hasMXNetStructure =
    Array.isArray(jsonData.nodes) &&
    Array.isArray(jsonData.arg_nodes) &&
    Array.isArray(jsonData.heads);

  return hasMXNetStructure;
}

/**
 * Parse MXNet model graph into Netron format
 */
export function parseMXNetGraph(jsonData, modelName = 'Model') {
  // Check if it's MXNet format
  if (!isMXNetFormat(jsonData)) {
    return {
      name: modelName,
      format: 'Unknown',
      producer: 'Unknown',
      version: '1.0',
      graphs: []
    };
  }

  console.log('✅ Detected MXNet format!');

  // Extract model information
  const result = {
    name: modelName,
    format: 'MXNet',
    producer: 'MXNet',
    version: '1.0',
    graphs: []
  };

  // Extract graph structure
  if (jsonData.nodes && jsonData.nodes.length > 0) {
    const graphInfo = {
      name: 'main',
      inputs: [],
      outputs: [],
      nodes: [],
      edges: []
    };

    // Map arg_nodes to get input indices
    const argNodeIndices = new Set(jsonData.arg_nodes || []);

    // Map head nodes to get outputs
    const outputNodes = (jsonData.heads || []).map((head) =>
      Array.isArray(head) ? head[0] : head
    );

    // First pass: collect node names for index mapping
    const indexToName = new Map();
    jsonData.nodes.forEach((node, index) => {
      if (!node) return;
      const nodeName = node.name || `node_${index}`;
      indexToName.set(index, nodeName);
    });

    // Process nodes and collect inputs/outputs
    jsonData.nodes.forEach((node, index) => {
      if (!node) return;

      const nodeInfo = {
        name: node.name || `node_${index}`,
        type: node.op || 'null',
        operator: node.op || 'null'
      };

      // Check if this is an input
      if (argNodeIndices.has(index)) {
        graphInfo.inputs.push({
          name: node.name || `input_${index}`,
          type: node.op || 'null'
        });
      }

      // Check if this is an output
      if (outputNodes.includes(index)) {
        graphInfo.outputs.push({
          name: node.name || `output_${index}`,
          type: node.op || 'null'
        });
      }

      graphInfo.nodes.push(nodeInfo);

      // Build edges from node inputs if present
      if (Array.isArray(node.inputs)) {
        for (const input of node.inputs) {
          // MXNet input entry format: [nodeId, index, version]
          const fromIndex = Array.isArray(input) ? input[0] : null;
          const toName = nodeInfo.name;
          const fromName = fromIndex != null ? indexToName.get(fromIndex) : null;
          if (fromName && toName && fromName !== toName) {
            graphInfo.edges.push({ from: fromName, to: toName });
          }
        }
      }
    });

    result.graphs.push(graphInfo);
  }

  // Extract additional metadata if available
  if (jsonData.attrs) {
    result.metadata = jsonData.attrs;
  }

  return result;
}
